// yard/yardStore.js - Zustand store for 3D Yard state management
import { create } from 'zustand';

// Size tier mapping based on deal value
const SIZE_TIERS = [
  { max: 5000, scale: 0.65 },
  { max: 15000, scale: 0.80 },
  { max: 30000, scale: 1.00 },
  { max: 60000, scale: 1.20 },
  { max: 100000, scale: 1.45 },
  { max: Infinity, scale: 1.80 }
];

// Calculate health score from deal data
export function calculateHealthScore(deal) {
  const now = Date.now();
  const lastActivity = deal.lastActivityDate ? new Date(deal.lastActivityDate).getTime() : now - 30 * 24 * 60 * 60 * 1000;
  const daysSinceActivity = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));

  // Base score starts at 100
  let score = 100;

  // Deduct for inactivity (max -50 points)
  score -= Math.min(daysSinceActivity * 2, 50);

  // Factor in close probability (0-1 scale, adds up to 30 points)
  const closeProb = deal.closeProbability || 0.5;
  score += (closeProb - 0.5) * 60;

  // Stage movement recency bonus
  if (deal.stageChangedDays !== undefined && deal.stageChangedDays < 7) {
    score += 15;
  }

  return Math.max(0, Math.min(100, score));
}

// Map health score to deal state
export function getDealState(healthScore) {
  if (healthScore >= 80) return 'ACTIVE';
  if (healthScore >= 40) return 'STALE';
  return 'COLD';
}

// Get size scale from deal value
export function getSizeScale(dealValue) {
  const tier = SIZE_TIERS.find(t => dealValue <= t.max);
  return tier ? tier.scale : 1.0;
}

// Pipeline stages for X-axis positioning
const PIPELINE_STAGES = [
  'Prospect',
  'Qualified',
  'Proposal',
  'Negotiation',
  'Closing',
  'Won',
  'active',
  'hot',
  'offer',
  'cold'
];

// Calculate dog position - SPREAD OUT version with elliptical boundary
export function calculateDogPosition(deal, index, totalDeals) {
  const yardWidth = 60;  // -30 to 30
  const yardDepth = 40;  // -20 to 20
  const padding = 5;

  if (totalDeals <= 1) {
    return { x: 0, y: 0, z: 0 };
  }

  if (totalDeals <= 4) {
    const positions = [
      { x: -15, z: -10 },
      { x: 15, z: -10 },
      { x: -15, z: 10 },
      { x: 15, z: 10 },
    ];
    const pos = positions[index % positions.length];
    const jitter = 3;
    return {
      x: pos.x + (Math.random() - 0.5) * jitter,
      y: 0,
      z: pos.z + (Math.random() - 0.5) * jitter
    };
  }

  if (totalDeals <= 9) {
    const gridX = index % 3;
    const gridZ = Math.floor(index / 3);
    const spacing = 12;
    const jitter = 2;
    return {
      x: (gridX - 1) * spacing + (Math.random() - 0.5) * jitter,
      y: 0,
      z: (gridZ - 1) * spacing + (Math.random() - 0.5) * jitter
    };
  }

  // 10+ dogs: golden angle spiral with ELLIPTICAL boundary
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const usableWidth = yardWidth - padding * 2;   // 50
  const usableDepth = yardDepth - padding * 2;   // 30

  // Scale spacing based on total count so dogs fill the yard proportionally
  const usableArea = Math.PI * (usableWidth / 2) * (usableDepth / 2);
  const areaPerDog = usableArea / totalDeals;
  const spacingFactor = Math.sqrt(areaPerDog / Math.PI) * 1.8;

  const radius = Math.sqrt(index + 0.5) * spacingFactor;
  const angle = index * goldenAngle;

  let x = Math.cos(angle) * radius;
  let z = Math.sin(angle) * radius;

  // Clamp to elliptical boundary instead of circular
  const maxX = usableWidth / 2;
  const maxZ = usableDepth / 2;
  const ellipseRadius = Math.sqrt((x * x) / (maxX * maxX) + (z * z) / (maxZ * maxZ));
  if (ellipseRadius > 1) {
    x /= ellipseRadius;
    z /= ellipseRadius;
  }

  // Less jitter as dog count increases to avoid overlap
  const jitter = Math.max(0.5, 2.0 - totalDeals * 0.01);

  return {
    x: x + (Math.random() - 0.5) * jitter,
    y: 0,
    z: z + (Math.random() - 0.5) * jitter
  };
}

// 12 Different squirrel path variations
const SQUIRREL_PATHS = [
  // Path 1: Straight across left to right
  { name: 'straight-lr', getPosition: (t) => ({ x: -35 + t * 70, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 8)) * 0.3, z: 0 }) },

  // Path 2: Straight across right to left
  { name: 'straight-rl', getPosition: (t) => ({ x: 35 - t * 70, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 8)) * 0.3, z: 0 }) },

  // Path 3: Diagonal top-left to bottom-right
  { name: 'diag-tlbr', getPosition: (t) => ({ x: -30 + t * 60, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 6)) * 0.4, z: -20 + t * 40 }) },

  // Path 4: Diagonal bottom-left to top-right
  { name: 'diag-bltr', getPosition: (t) => ({ x: -30 + t * 60, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 6)) * 0.4, z: 20 - t * 40 }) },

  // Path 5: Zigzag horizontal (3 turns)
  { name: 'zigzag-h', getPosition: (t) => ({ x: -35 + t * 70, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 10)) * 0.3, z: Math.sin(t * Math.PI * 3) * 15 }) },

  // Path 6: Zigzag horizontal (5 turns) - more erratic
  { name: 'zigzag-h2', getPosition: (t) => ({ x: -35 + t * 70, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 12)) * 0.4, z: Math.sin(t * Math.PI * 5) * 12 }) },

  // Path 7: S-curve
  { name: 's-curve', getPosition: (t) => ({ x: -35 + t * 70, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 8)) * 0.3, z: Math.sin(t * Math.PI * 2) * 18 }) },

  // Path 8: Figure-8 pattern (partial)
  { name: 'figure8', getPosition: (t) => ({ x: Math.sin(t * Math.PI * 2) * 25, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 8)) * 0.4, z: Math.sin(t * Math.PI * 4) * 15 }) },

  // Path 9: Spiral outward
  { name: 'spiral-out', getPosition: (t) => {
    const angle = t * Math.PI * 4;
    const radius = t * 25;
    return { x: Math.cos(angle) * radius, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 10)) * 0.3, z: Math.sin(angle) * radius };
  }},

  // Path 10: Back and forth (teasing the dogs)
  { name: 'tease', getPosition: (t) => {
    const phase = t * 3;
    const direction = Math.floor(phase) % 2 === 0 ? 1 : -1;
    const localT = phase % 1;
    return { x: direction * (localT * 30 - 15), y: 0.5 + Math.abs(Math.sin(t * Math.PI * 12)) * 0.5, z: Math.sin(t * Math.PI * 6) * 10 };
  }},

  // Path 11: Perimeter run (around the edge)
  { name: 'perimeter', getPosition: (t) => {
    const segment = t * 4;
    const localT = segment % 1;
    const side = Math.floor(segment) % 4;
    let x, z;
    if (side === 0) { x = -25 + localT * 50; z = -18; }
    else if (side === 1) { x = 25; z = -18 + localT * 36; }
    else if (side === 2) { x = 25 - localT * 50; z = 18; }
    else { x = -25; z = 18 - localT * 36; }
    return { x, y: 0.5 + Math.abs(Math.sin(t * Math.PI * 16)) * 0.3, z };
  }},

  // Path 12: Crazy random-ish (but deterministic based on t)
  { name: 'crazy', getPosition: (t) => ({
    x: Math.sin(t * 7.3) * 25 + Math.cos(t * 13.1) * 8,
    y: 0.5 + Math.abs(Math.sin(t * Math.PI * 15)) * 0.5,
    z: Math.cos(t * 5.7) * 15 + Math.sin(t * 11.3) * 5
  })}
];

// Main yard store
export const useYardStore = create((set, get) => ({
  // Dogs (deals mapped to visual entities)
  dogs: [],

  // Squirrel event state
  squirrelEvent: {
    active: false,
    position: { x: -30, y: 0.5, z: 0 },
    progress: 0,
    cooldown: false,
    currentPath: 0 // Which path variation to use
  },

  // Camera state
  camera: {
    target: { x: 0, y: 5, z: 0 },
    position: { x: 0, y: 25, z: 35 },
    focusedDogId: null
  },

  // Filters
  filters: {
    owner: 'all',
    stage: 'all',
    state: 'all'
  },

  // Selected dog (for modal dimming)
  selectedDogId: null,

  selectDog: (dogId) => set({ selectedDogId: dogId }),
  deselectDog: () => set({ selectedDogId: null }),

  // Performance settings
  performance: {
    animationDistance: 30,
    lodDistance: 35,
    maxVisibleDogs: 200
  },

  // Initialize dogs from deals data
  initializeDogs: (deals) => {
    console.log('Initializing dogs from deals:', deals.length, deals);

    const dogs = deals.map((deal, index) => {
      // Use healthScore from API if available, otherwise calculate
      const healthScore = deal.healthScore || calculateHealthScore(deal);

      // Map healthState from API to our state format (ACTIVE/STALE/COLD)
      let dealState;
      if (deal.healthState) {
        const hs = deal.healthState.toLowerCase();
        dealState = hs === 'hot' ? 'ACTIVE' : hs === 'stale' ? 'STALE' : hs === 'cold' ? 'COLD' : 'ACTIVE';
      } else {
        dealState = getDealState(healthScore);
      }

      const scale = getSizeScale(deal.value || deal.fee || 15000);
      const position = calculateDogPosition(deal, index, deals.length);

      console.log(`Dog ${index}: ${deal.title || deal.JobTitle}, state=${dealState}, health=${healthScore}, scale=${scale}`);

      return {
        id: deal.id || deal.JobId || `dog-${index}`,
        dealId: deal.id || deal.JobId,
        deal: deal,
        position: position,
        homePosition: { ...position },
        rotation: Math.random() * Math.PI * 2,
        scale: scale,
        healthScore: healthScore,
        dealState: dealState,
        eventState: null, // CHASE_SQUIRREL or null
        animation: dealState === 'ACTIVE' ? 'walk' : dealState === 'STALE' ? 'sit' : 'lay',
        tint: dealState === 'ACTIVE' ? '#ffffff' : dealState === 'STALE' ? '#88aaff' : '#4466cc',
        velocity: { x: 0, z: 0 },
        chaseSpeed: 5 + Math.random() * 3 // Vary speed
      };
    });

    console.log('Dogs initialized:', dogs.length);
    set({ dogs });
  },

  // Update a single dog
  updateDog: (dogId, updates) => {
    set(state => ({
      dogs: state.dogs.map(dog =>
        dog.id === dogId ? { ...dog, ...updates } : dog
      )
    }));
  },

  // Start squirrel chase event
  dropSquirrel: () => {
    const state = get();
    if (state.squirrelEvent.active || state.squirrelEvent.cooldown) return;

    // Randomly select a path variation (0-11 for 12 paths)
    const randomPath = Math.floor(Math.random() * SQUIRREL_PATHS.length);
    const startPos = SQUIRREL_PATHS[randomPath].getPosition(0);

    // Start event
    set({
      squirrelEvent: {
        active: true,
        position: startPos,
        progress: 0,
        cooldown: false,
        currentPath: randomPath
      }
    });

    // Set all dogs to chase mode
    set(state => ({
      dogs: state.dogs.map(dog => ({
        ...dog,
        eventState: 'CHASE_SQUIRREL',
        animation: 'run',
        tint: '#ffffff' // All dogs "revive" during chase
      }))
    }));
  },

  // Update squirrel position (called each frame)
  updateSquirrel: (delta) => {
    const state = get();
    if (!state.squirrelEvent.active) return;

    const newProgress = state.squirrelEvent.progress + delta * 0.2; // ~5 second run

    if (newProgress >= 1) {
      // End event
      get().endSquirrelEvent();
      return;
    }

    // Get position from current path
    const pathIndex = state.squirrelEvent.currentPath;
    const path = SQUIRREL_PATHS[pathIndex];
    const newPosition = path.getPosition(newProgress);

    set({
      squirrelEvent: {
        ...state.squirrelEvent,
        position: newPosition,
        progress: newProgress
      }
    });
  },

  // End squirrel event and restore dogs
  endSquirrelEvent: () => {
    // Restore dogs to their deal states
    set(state => ({
      dogs: state.dogs.map(dog => ({
        ...dog,
        eventState: null,
        animation: dog.dealState === 'ACTIVE' ? 'walk' : dog.dealState === 'STALE' ? 'sit' : 'lay',
        tint: dog.dealState === 'ACTIVE' ? '#ffffff' : dog.dealState === 'STALE' ? '#88aaff' : '#4466cc'
      })),
      squirrelEvent: {
        active: false,
        position: { x: -30, y: 0.5, z: 0 },
        progress: 0,
        cooldown: true,
        currentPath: 0
      }
    }));

    // Reset cooldown after 5 seconds (reduced from 10)
    setTimeout(() => {
      set(state => ({
        squirrelEvent: { ...state.squirrelEvent, cooldown: false }
      }));
    }, 5000);
  },

  // Update dog positions during chase
  updateDogsChase: (delta) => {
    const state = get();
    if (!state.squirrelEvent.active) return;

    const squirrelPos = state.squirrelEvent.position;

    set({
      dogs: state.dogs.map(dog => {
        if (dog.eventState !== 'CHASE_SQUIRREL') return dog;

        // Calculate direction to squirrel
        const dx = squirrelPos.x - dog.position.x;
        const dz = squirrelPos.z - dog.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < 0.1) return dog;

        // Normalize and apply speed
        const speed = dog.chaseSpeed * delta;
        const nx = (dx / dist) * speed;
        const nz = (dz / dist) * speed;

        // Update position
        const newX = dog.position.x + nx;
        const newZ = dog.position.z + nz;

        // Calculate rotation towards movement direction
        const rotation = Math.atan2(dx, dz);

        return {
          ...dog,
          position: { ...dog.position, x: newX, z: newZ },
          rotation: rotation
        };
      })
    });
  },

  // Return dogs to home positions after chase
  updateDogsReturn: (delta) => {
    const state = get();
    if (state.squirrelEvent.active) return;

    const returnSpeed = 3;

    set({
      dogs: state.dogs.map(dog => {
        const dx = dog.homePosition.x - dog.position.x;
        const dz = dog.homePosition.z - dog.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < 0.1) {
          return { ...dog, position: { ...dog.homePosition } };
        }

        const speed = returnSpeed * delta;
        const t = Math.min(speed / dist, 1);

        return {
          ...dog,
          position: {
            x: dog.position.x + dx * t,
            y: dog.position.y,
            z: dog.position.z + dz * t
          },
          rotation: dist > 0.5 ? Math.atan2(dx, dz) : dog.rotation
        };
      })
    });
  },

  // Set filter
  setFilter: (filterType, value) => {
    set(state => ({
      filters: { ...state.filters, [filterType]: value }
    }));
  },

  // Focus camera on a dog
  focusOnDog: (dogId) => {
    const dog = get().dogs.find(d => d.id === dogId);
    if (!dog) return;

    set({
      camera: {
        target: { x: dog.position.x, y: 2, z: dog.position.z },
        position: { x: dog.position.x, y: 15, z: dog.position.z + 20 },
        focusedDogId: dogId
      }
    });
  },

  // Reset camera
  resetCamera: () => {
    set({
      camera: {
        target: { x: 0, y: 5, z: 0 },
        position: { x: 0, y: 25, z: 35 },
        focusedDogId: null
      }
    });
  }
}));

export default useYardStore;
